import Add from "@mui/icons-material/Add";
import Calculate from "@mui/icons-material/Calculate";
import DeleteOutline from "@mui/icons-material/DeleteOutlined";
import Edit from "@mui/icons-material/Edit";
import ListAlt from "@mui/icons-material/ListAlt";
import TableRows from "@mui/icons-material/TableRows";
import ViewList from "@mui/icons-material/ViewList";
import {
	Avatar,
	Box,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Paper,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import type { MouseEvent, ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";

import type { Option } from "../constants";
import type { VariableGroupV2 } from "../fields/form-entity-select";
import FormulaBuilderForm, { type FormulaBuilderFormState } from "../forms/FormulaBuilderForm";
import type { ScreenLocationOption } from "../modals/CreateRuleModal";
import SimpleVariableForm, { type SimpleVariableFormState } from "../forms/SimpleVariableForm";
import { appScrollbarSx } from "../forms/styled/CanvasFormTextField.styled";
import type { FormulaVariableInput,RuleVariable, SimpleVariableInput } from "../hooks/useRuleVariables";
import { normalizeVariableFormulaFromApi } from "../utils/formula-variable-payload";
import { loopCurrentRowAsSingleRowDatabaseVariable } from "../utils/loop-resource-variables";
import { SearchElementsField } from "./SearchElementsField";
import { ToolboxPanel } from "./styled/toolbox.styled";

type ToolboxProps = {
	databaseSingleRowVariables: RuleVariable[];
	databaseMultiRowVariables: RuleVariable[];
	simpleVariables: RuleVariable[];
	formulaVariables: RuleVariable[];
	reactionVariables: RuleVariable[];
	onAddSimpleVariable: (variable: SimpleVariableInput) => RuleVariable | null;
	onUpdateSimpleVariable: (id: string, variable: SimpleVariableInput) => boolean;
	onRemoveSimpleVariable: (id: string) => void;
	onAddFormulaVariable: (variable: FormulaVariableInput) => RuleVariable | null;
	onUpdateFormulaVariable: (id: string, variable: FormulaVariableInput) => boolean;
	onRemoveFormulaVariable: (id: string) => void;
	onDatabaseVariableClick?: (variable: RuleVariable) => void;
	onReactionVariableClick?: (variable: RuleVariable) => void;
	formulaOperatorOptions?: Option[];
	expressionFunctionOptions?: Option[];
	isFormMetadataLoading?: boolean;
	/** Passed through to FormulaBuilderForm Resource picker (same as Decision/ Reaction). */
	platformId?: string | number;
	resourceVariableGroups?: VariableGroupV2[];
	formulaVariableOptions?: Option[];
	singleRowDatabaseVariableOptions?: Option[];
	resourceScreenFields?: Option[];
	resourceAdvancedTriggers?: Option[];
	screenLocationOptions?: ScreenLocationOption[];
	ruleScreenLocationId?: string | number;
	globalConstantOptions?: Option[];
	/**
	 * Full rule variable list for form validation when display lists are filtered (e.g. loop sub-canvas).
	 */
	fullVariableUniverse?: RuleVariable[];
	/** When false, the multi-row DB variable pills section is hidden (e.g. inside a loop sub-canvas). */
	showDatabaseMultiRowSection?: boolean;
	/** True while a loop body's canvas is active (loop contents open). For search / list filtering. */
	inLoopCanvas?: boolean;
	/** Loop sub-canvas: multi-row DB variable this loop iterates. Shows as "Current Row — …" in Single Row group. */
	loopIteratedDatabaseMultiVariable?: RuleVariable | null;
};

type VariablePillProps = {
	variable: RuleVariable;
	color: string;
	icon: ReactElement;
	onClick?: () => void;
	onContextMenu?: (event: MouseEvent) => void;
	onDelete?: () => void;
};

type VariableSectionProps = {
	title: string;
	count: number;
	action?: ReactElement | null;
	children: ReactElement | ReactElement[];
};

const VariablePill = ({ variable, color, icon, onClick, onContextMenu, onDelete }: VariablePillProps) => (
	<Paper
		variant="outlined"
		onClick={onClick}
		onContextMenu={onContextMenu}
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			px: 1.25,
			py: 1,
			borderRadius: 2,
			borderColor: "divider",
			backgroundColor: "#fafbfc",
			cursor: onClick ? "pointer" : "default",
			"& .delete-btn": { opacity: 0 },
			"&:hover .delete-btn": { opacity: 1 },
			"&:hover": onClick ? {
				borderColor: color,
				backgroundColor: `${color}08`,
			} : {},
		}}
	>
		<Avatar
			variant="rounded"
			sx={{
				width: 30,
				height: 30,
				backgroundColor: `${color}1A`,
				color,
				fontSize: 18,
				border: `1px solid ${color}33`,
			}}
		>
			{icon}
		</Avatar>
		<Box sx={{ minWidth: 0, flex: 1 }}>
			<Typography variant="body2" fontWeight={500} noWrap title={variable.name}>
				{variable.name}
			</Typography>
			{variable.source ? (
				<Typography variant="caption" color="text.secondary" noWrap title={variable.source}>
					{variable.source}
				</Typography>
			) : null}
		</Box>
		{onDelete && (
			<Tooltip title="Delete variable" arrow>
				<IconButton
					className="delete-btn"
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						onDelete();
					}}
					sx={{
						ml: "auto",
						color: "error.main",
						transition: "opacity 150ms",
						"&:hover": { backgroundColor: "error.light", color: "error.contrastText" },
					}}
				>
					<DeleteOutline fontSize="small" />
				</IconButton>
			</Tooltip>
		)}
	</Paper>
);

const VariableSection = ({ title, count, action = null, children }: VariableSectionProps) => (
	<Stack spacing={1.25}>
		<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
			<Stack direction="row" spacing={0.75} alignItems="baseline">
				<Typography variant="body2" fontWeight={500}>
					{title}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					({count})
				</Typography>
			</Stack>
			{action}
		</Stack>
		{children}
	</Stack>
);

const Toolbox = (props: ToolboxProps) => {
	const {
		databaseSingleRowVariables,
		databaseMultiRowVariables,
		simpleVariables,
		formulaVariables,
		reactionVariables,
		onAddSimpleVariable,
		onUpdateSimpleVariable,
		onRemoveSimpleVariable,
		onAddFormulaVariable,
		onUpdateFormulaVariable,
		onRemoveFormulaVariable,
		onDatabaseVariableClick,
		onReactionVariableClick,
		formulaOperatorOptions = [],
		expressionFunctionOptions = [],
		isFormMetadataLoading = false,
		platformId,
		resourceVariableGroups,
		formulaVariableOptions,
		singleRowDatabaseVariableOptions,
		resourceScreenFields,
		resourceAdvancedTriggers = [],
		screenLocationOptions = [],
		ruleScreenLocationId,
		globalConstantOptions = [],
		fullVariableUniverse,
		showDatabaseMultiRowSection = true,
		inLoopCanvas = false,
		loopIteratedDatabaseMultiVariable = null,
	} = props;
	const theme = useTheme();
	const [isSimpleFormOpen, setIsSimpleFormOpen] = useState(false);
	const [isFormulaFormOpen, setIsFormulaFormOpen] = useState(false);
	const [editingVariable, setEditingVariable] = useState<RuleVariable | null>(null);
	const [editingFormulaVariable, setEditingFormulaVariable] = useState<RuleVariable | null>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [variableContextMenu, setVariableContextMenu] = useState<{
		mouseX: number;
		mouseY: number;
		variable: RuleVariable;
	} | null>(null);

	const handleVariableContextMenu = useCallback((event: MouseEvent, variable: RuleVariable) => {
		event.preventDefault();
		setVariableContextMenu({
			mouseX: event.clientX,
			mouseY: event.clientY,
			variable,
		});
	}, []);

	const handleVariableContextMenuClose = useCallback(() => {
		setVariableContextMenu(null);
	}, []);

	const handleVariableContextMenuDelete = useCallback(() => {
		if (!variableContextMenu) return;
		const { variable } = variableContextMenu;
		if (variable.type === "simple") {
			onRemoveSimpleVariable(variable.id);
		} else if (variable.type === "formula") {
			onRemoveFormulaVariable(variable.id);
		}
		setVariableContextMenu(null);
	}, [variableContextMenu, onRemoveSimpleVariable, onRemoveFormulaVariable]);

	const allVariables = useMemo(
		() =>
			fullVariableUniverse ?? [
				...databaseSingleRowVariables,
				...databaseMultiRowVariables,
				...simpleVariables,
				...formulaVariables,
				...reactionVariables,
			],
		[
			fullVariableUniverse,
			databaseMultiRowVariables,
			databaseSingleRowVariables,
			formulaVariables,
			simpleVariables,
			reactionVariables,
		],
	);

	const handleSimpleVariableSave = (input: SimpleVariableFormState & { dataTypeId: number }) => {
		if (editingVariable) {
			const success = onUpdateSimpleVariable(editingVariable.id, input);
			if (success) {
				setIsSimpleFormOpen(false);
				setEditingVariable(null);
			}
		} else {
			const created = onAddSimpleVariable(input);
			if (created) {
				setIsSimpleFormOpen(false);
			}
		}
	};

	const handleEditVariable = (variable: RuleVariable) => {
		setEditingVariable(variable);
		setIsSimpleFormOpen(true);
	};

	const handleCloseForm = () => {
		setIsSimpleFormOpen(false);
		setEditingVariable(null);
	};

	const handleFormulaVariableSave = (input: FormulaBuilderFormState & { dataTypeId: number }) => {
		const variableInput: FormulaVariableInput = {
			name: input.name,
			dataTypeId: input.dataTypeId,
			variableFormula: input.variableFormula,
		};
		if (editingFormulaVariable) {
			const success = onUpdateFormulaVariable(editingFormulaVariable.id, variableInput);
			if (success) {
				setIsFormulaFormOpen(false);
				setEditingFormulaVariable(null);
			}
		} else {
			const created = onAddFormulaVariable(variableInput);
			if (created) {
				setIsFormulaFormOpen(false);
			}
		}
	};

	const handleEditFormulaVariable = (variable: RuleVariable) => {
		setEditingFormulaVariable(variable);
		setIsFormulaFormOpen(true);
	};

	const handleCloseFormulaForm = () => {
		setIsFormulaFormOpen(false);
		setEditingFormulaVariable(null);
	};

	const filterVariables = (variables: RuleVariable[]) => {
		if (!searchQuery.trim()) return variables;
		const query = searchQuery.toLowerCase();
		return variables.filter((variable) =>
			variable.name.toLowerCase().includes(query) ||
			variable.source?.toLowerCase().includes(query)
		);
	};

	const currentRowSingleRowPillVariable = useMemo((): RuleVariable | null => {
		if (!inLoopCanvas || !loopIteratedDatabaseMultiVariable) return null;
		if (!loopIteratedDatabaseMultiVariable.name?.trim()) return null;
		return loopCurrentRowAsSingleRowDatabaseVariable(loopIteratedDatabaseMultiVariable);
	}, [inLoopCanvas, loopIteratedDatabaseMultiVariable]);

	const ruleDatabaseSingleRowForResourceDropdowns = useMemo(() => {
		if (!inLoopCanvas || !loopIteratedDatabaseMultiVariable) return databaseSingleRowVariables;
		const row = loopCurrentRowAsSingleRowDatabaseVariable(loopIteratedDatabaseMultiVariable);
		return [row, ...databaseSingleRowVariables.filter((v) => v.id !== row.id)];
	}, [inLoopCanvas, loopIteratedDatabaseMultiVariable, databaseSingleRowVariables]);

	const ruleDatabaseMultiRowForResourceDropdowns = useMemo(
		() => (inLoopCanvas ? [] : databaseMultiRowVariables),
		[inLoopCanvas, databaseMultiRowVariables],
	);

	const filteredDatabaseSingleRowVariables = useMemo(
		() => filterVariables(databaseSingleRowVariables),
		[databaseSingleRowVariables, searchQuery],
	);

	const filteredCurrentRowPill = useMemo(() => {
		if (!currentRowSingleRowPillVariable) return null;
		return filterVariables([currentRowSingleRowPillVariable]).length > 0
			? currentRowSingleRowPillVariable
			: null;
	}, [currentRowSingleRowPillVariable, searchQuery]);

	const filteredDatabaseMultiRowVariables = useMemo(
		() => filterVariables(databaseMultiRowVariables),
		[databaseMultiRowVariables, searchQuery],
	);

	const filteredSimpleVariables = useMemo(
		() => filterVariables(simpleVariables),
		[simpleVariables, searchQuery],
	);

	const filteredFormulaVariables = useMemo(
		() => filterVariables(formulaVariables),
		[formulaVariables, searchQuery],
	);

	const filteredReactionVariables = useMemo(
		() => filterVariables(reactionVariables),
		[reactionVariables, searchQuery],
	);

	const isAnyVariableFormOpen = isSimpleFormOpen || isFormulaFormOpen;

	return (
		<ToolboxPanel
			elevation={1}
			data-in-loop-canvas={inLoopCanvas ? "true" : undefined}
		>
			{/* Toolbox header + variable sections — hidden whenever a variable
			    form is open so the form panel takes over the entire left
			    column instead of stacking under it. */}
			{!isAnyVariableFormOpen && (
				<Box sx={{ px: 2.5, py: 2.25, borderBottom: "1px solid", borderColor: "divider" }}>
					<SearchElementsField
						value={searchQuery}
						onChange={setSearchQuery}
					/>
				</Box>
			)}

			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					p: isAnyVariableFormOpen ? 0 : 2.5,
					display: isAnyVariableFormOpen ? "contents" : "block",
					...appScrollbarSx
				}}
			>
				{!isAnyVariableFormOpen && (
					<Stack spacing={2.5}>
						{/* Single Row Variables — auto-derived from query/filter/sort nodes.
					    Hidden until populated to keep the toolbox compact. */}
						{(filteredCurrentRowPill || filteredDatabaseSingleRowVariables.length > 0) && (
							<>
								<VariableSection
									title="Single Row Variables"
									count={databaseSingleRowVariables.length + (currentRowSingleRowPillVariable ? 1 : 0)}
								>
									<Stack spacing={1}>
										{filteredCurrentRowPill ? (
											<VariablePill
												key={filteredCurrentRowPill.id}
												variable={filteredCurrentRowPill}
												color={theme.palette.primary.main}
												icon={<TableRows fontSize="small" />}
												onClick={
													onDatabaseVariableClick && loopIteratedDatabaseMultiVariable
														? () => onDatabaseVariableClick(loopIteratedDatabaseMultiVariable)
														: undefined
												}
											/>
										) : null}
										{filteredDatabaseSingleRowVariables.map((variable) => (
											<VariablePill
												key={variable.id}
												variable={variable}
												color={theme.palette.primary.main}
												icon={<TableRows fontSize="small" />}
												onClick={onDatabaseVariableClick ? () => onDatabaseVariableClick(variable) : undefined}
											/>
										))}
									</Stack>
								</VariableSection>
								<Divider sx={{ mx: -2.5 }} />
							</>
						)}

						{/* Multi Row Variables — same pattern as above. */}
						{showDatabaseMultiRowSection && filteredDatabaseMultiRowVariables.length > 0 && (
							<>
								<VariableSection title="Multi Row Variables" count={databaseMultiRowVariables.length}>
									<Stack spacing={1}>
										{filteredDatabaseMultiRowVariables.map((variable) => (
											<VariablePill
												key={variable.id}
												variable={variable}
												color={theme.palette.info.main}
												icon={<ViewList fontSize="small" />}
												onClick={onDatabaseVariableClick ? () => onDatabaseVariableClick(variable) : undefined}
											/>
										))}
									</Stack>
								</VariableSection>
								<Divider sx={{ mx: -2.5 }} />
							</>
						)}

						<VariableSection
							title="Simple Variables"
							count={simpleVariables.length}
						>
							<Stack spacing={1}>
								{filteredSimpleVariables.map((variable) => (
									<VariablePill
										key={variable.id}
										variable={variable}
										color={theme.palette.primary.main}
										icon={<Edit fontSize="small" />}
										onClick={() => handleEditVariable(variable)}
										onContextMenu={(e) => handleVariableContextMenu(e, variable)}
										onDelete={() => onRemoveSimpleVariable(variable.id)}
									/>
								))}
								<Paper
									variant="outlined"
									onClick={() => setIsSimpleFormOpen(true)}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										px: 1.25,
										py: 1,
										borderRadius: 2,
										borderColor: "divider",
										backgroundColor: "#fafbfc",
										cursor: "pointer",
										"&:hover": {
											borderColor: theme.palette.success.main,
											backgroundColor: `${theme.palette.success.main}08`,
										},
									}}
								>
									<Avatar
										variant="rounded"
										sx={{
											width: 30,
											height: 30,
											backgroundColor: `${theme.palette.primary.main}1A`,
											color: theme.palette.secondary.main,
											fontSize: 18,
											border: `1px solid ${theme.palette.secondary.main}33`,
										}}
									>
										<Add fontSize="small" />
									</Avatar>
									<Typography variant="body2" sx={{ fontWeight: 500 }} color={theme.palette.secondary.main}>
									Add Simple Variable
									</Typography>
								</Paper>
							</Stack>
						</VariableSection>

						<Divider sx={{ mx: -2.5 }} />

						<VariableSection title="Formula Variables" count={formulaVariables.length}>
							<Stack spacing={1}>
								{filteredFormulaVariables.map((variable) => (
									<VariablePill
										key={variable.id}
										variable={variable}
										color={theme.palette.warning.main}
										icon={<Edit fontSize="small" />}
										onClick={() => handleEditFormulaVariable(variable)}
										onContextMenu={(e) => handleVariableContextMenu(e, variable)}
										onDelete={() => onRemoveFormulaVariable(variable.id)}
									/>
								))}
								<Paper
									variant="outlined"
									onClick={() => setIsFormulaFormOpen(true)}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										px: 1.25,
										py: 1,
										borderRadius: 2,
										borderColor: "divider",
										backgroundColor: "#fafbfc",
										cursor: "pointer",
										"&:hover": {
											borderColor: theme.palette.success.main,
											backgroundColor: `${theme.palette.success.main}08`,
										},
									}}
								>
									<Avatar
										variant="rounded"
										sx={{
											width: 30,
											height: 30,
											backgroundColor: `${theme.palette.primary.main}1A`,
											color: theme.palette.secondary.main,
											fontSize: 18,
											border: `1px solid ${theme.palette.secondary.main}33`,
										}}
									>
										<Add fontSize="small" />
									</Avatar>
									<Typography variant="body2" sx={{ fontWeight: 500 }} color={theme.palette.secondary.main}>
									Add Formula Variable
									</Typography>
								</Paper>
							</Stack>
						</VariableSection>

						{/* Reactions — auto-derived from reaction nodes. Hidden until populated. */}
						{filteredReactionVariables.length > 0 && (
							<>
								<Divider sx={{ mx: -2.5 }} />
								<VariableSection title="Reactions" count={reactionVariables.length}>
									<Stack spacing={1}>
										{filteredReactionVariables.map((variable) => (
											<VariablePill
												key={variable.id}
												variable={variable}
												color="#E63946"
												icon={<ListAlt fontSize="small" />}
												onClick={onReactionVariableClick ? () => onReactionVariableClick(variable) : undefined}
											/>
										))}
									</Stack>
								</VariableSection>
							</>
						)}
					</Stack>
				)}
			</Box>
			<SimpleVariableForm
				open={isSimpleFormOpen}
				onClose={handleCloseForm}
				onSave={handleSimpleVariableSave}
				existingVariables={allVariables}
				editingVariableId={editingVariable?.id ?? null}
				platformId={platformId}
				resourceVariableGroups={resourceVariableGroups}
				formulaVariableOptions={formulaVariableOptions}
				singleRowDatabaseVariableOptions={singleRowDatabaseVariableOptions}
				resourceScreenFields={resourceScreenFields}
				globalConstantOptions={globalConstantOptions}
				ruleSimpleVariables={simpleVariables}
				ruleFormulaVariables={formulaVariables}
				ruleDatabaseSingleRowVariables={ruleDatabaseSingleRowForResourceDropdowns}
				ruleDatabaseMultiRowVariables={ruleDatabaseMultiRowForResourceDropdowns}
				initialState={editingVariable ? {
					name: editingVariable.name,
					dataTypeId: editingVariable.dataTypeId,
					valueLiteral: editingVariable.valueLiteral,
					valueDatabaseTableFieldId: editingVariable.valueDatabaseTableFieldId,
					valueScreenFieldId: editingVariable.valueScreenFieldId,
					valueDatabaseTableFieldName: editingVariable.valueDatabaseTableFieldName,
					valueScreenFieldName: editingVariable.valueScreenFieldName,
					valueScreenField: editingVariable.valueScreenField,
					valueSimpleVariableName: editingVariable.valueSimpleVariableName,
					valueSimpleVariableId: editingVariable.valueSimpleVariableId,
					valueFormulaVariableId: editingVariable.valueFormulaVariableId,
					valueFormulaVariableName: editingVariable.valueFormulaVariableName,
					valueDatabaseSingleRowVariableId: editingVariable.valueDatabaseSingleRowVariableId,
					valueDatabaseSingleRowVariableName: editingVariable.valueDatabaseSingleRowVariableName,
					valueGlobalConstant: editingVariable.valueGlobalConstant,
					valueGlobalConstantName: editingVariable.valueGlobalConstantName,
					valueCategory: editingVariable.valueCategory,
				} : undefined}
			/>
			<FormulaBuilderForm
				open={isFormulaFormOpen}
				onClose={handleCloseFormulaForm}
				onSave={handleFormulaVariableSave}
				existingVariables={allVariables}
				editingVariableId={editingFormulaVariable?.id ?? null}
				initialState={editingFormulaVariable ? {
					name: editingFormulaVariable.name,
					dataTypeId: editingFormulaVariable.dataTypeId,
					variableFormula: normalizeVariableFormulaFromApi(editingFormulaVariable.variableFormula),
				} : undefined}
				formulaOperatorOptions={formulaOperatorOptions}
				expressionFunctionOptions={expressionFunctionOptions}
				isFormMetadataLoading={isFormMetadataLoading}
				platformId={platformId}
				resourceVariableGroups={resourceVariableGroups}
				formulaVariableOptions={formulaVariableOptions}
				singleRowDatabaseVariableOptions={singleRowDatabaseVariableOptions}
				resourceScreenFields={resourceScreenFields}
				resourceAdvancedTriggers={resourceAdvancedTriggers}
				screenLocationOptions={screenLocationOptions}
				ruleScreenLocationId={ruleScreenLocationId}
			/>
			<Menu
				open={variableContextMenu !== null}
				onClose={handleVariableContextMenuClose}
				anchorReference="anchorPosition"
				anchorPosition={
					variableContextMenu !== null
						? { top: variableContextMenu.mouseY, left: variableContextMenu.mouseX }
						: undefined
				}
				slotProps={{ list: { dense: true } }}
			>
				<MenuItem onClick={handleVariableContextMenuDelete}>
					<ListItemIcon>
						<DeleteOutline fontSize="small" color="error" />
					</ListItemIcon>
					<ListItemText primary="Delete Variable" />
				</MenuItem>
			</Menu>
		</ToolboxPanel>
	);
};

export default Toolbox;
